import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { RippleModule } from 'primeng/ripple';
import { ThemeService } from '../../services/theme.service';
import { AuthService } from '../../services/auth.service';
import { ThemeHelper } from '../../helper/theme.helper';
import { CommonModule } from '@angular/common';
import { PublicSettingDto } from '../../helper/dto/public-settings.dto';
import { AuthApiService } from '../../services/auth-api.service';
import { TwoFactorDto } from '../../helper/dto/two-factor.dto';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { validateEmail } from '../../helper/auth.helper';
import { SelectModule } from 'primeng/select';
import { ToastService } from '../../services/toast.service';
import { consumeLogoutMarker } from '../../helper/logout-marker.helper';

@Component({
    selector: 'app-login',
    standalone: true,
    imports: [CommonModule, ButtonModule, CheckboxModule, InputTextModule, PasswordModule, FormsModule, RouterModule, RippleModule, TranslatePipe, SelectModule],
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit {
    private destroyRef = inject(DestroyRef);

    themeData: ThemeHelper | undefined;
    oidcData: PublicSettingDto[] = [];

    oidcEnabled = false;
    oidcForceLogin = false;
    oidcButtonText = '';
    showSpinner = false;

    credentials = {
        email: '',
        password: ''
    };

    validationErrors: { [key: string]: string } = {};

    twofa = {
        code: '',
        selectedDevice: ''
    };
    stage = 'login';

    twoFactorDevices: TwoFactorDto[] = [];

    private justLoggedOut = false;

    constructor(
        private readonly router: Router,
        private themeService: ThemeService,
        private authService: AuthService,
        private authApiService: AuthApiService,
        private route: ActivatedRoute,
        private translateService: TranslateService,
        private toastService: ToastService
    ) { }

    ngOnInit(): void {
        this.justLoggedOut = consumeLogoutMarker();
        this.themeService.getData();

        this.themeService.themeData
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((theme) => {
                this.themeData = theme;
                this.themeService.updateTitle(`Login`);
            });

        this.authApiService.getOIDCData()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (data) => {
                    this.oidcData = data;

                    const oidcButtonText = this.oidcData.find((x: any) => x.key === 'oidc_login_button_text');
                    const oidcEnable = this.oidcData.find((x: any) => x.key === 'oidc_enabled');
                    const oidcForceLogin = this.oidcData.find((x: any) => x.key === 'oidc_force_login');

                    this.oidcEnabled = !!(oidcEnable?.valueBoolean);
                    this.oidcForceLogin = !!(oidcForceLogin?.valueBoolean);

                    if (oidcButtonText?.value) {
                        this.oidcButtonText = oidcButtonText.value;
                    }

                    this.watchSession();
                },
                error: (error) => {
                    console.error(error);
                    this.showSpinner = false;
                    this.watchSession();
                }
            });
    }

    private watchSession(): void {
        this.authService.sessionData
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (data) => {
                    if ((!data.isLoggedIn && data.require2Fa) || data.tokenType === 'twofactortoken') {
                        this.stage = '2fa';

                        if (data.twofactors) {
                            this.twoFactorDevices = data.twofactors;

                            if (data.twofactors.length === 1) {
                                this.twofa.selectedDevice = data.twofactors[0].name!;
                            }
                        }
                    }

                    if (data.isLoggedIn && data.user.expires > Date.now() && data.tokenType !== 'twofactortoken') {
                        this.redirectAfterLogin();
                    } else if (
                        !data.isLoggedIn &&
                        !data.require2Fa &&
                        !this.justLoggedOut &&
                        this.oidcEnabled &&
                        this.oidcForceLogin &&
                        !this.router.url.includes('localLogin=true')
                    ) {
                        this.oidcLogin();
                    } else {
                        this.showSpinner = false;
                    }
                },
                error: (error) => console.error(error)
            });
    }

    /**
     * Leitet nach erfolgreichem Login weiter – zur ursprünglich angeforderten Route
     * (`returnUrl`, z. B. bei Deep-Links in einem neuen Tab) oder ersatzweise zur Startseite.
     * Wird an allen Weiterleitungspfaden verwendet, damit ein re-entranter Session-Emit
     * die `returnUrl` nicht überschreibt.
     */
    private redirectAfterLogin(): void {
        this.route.queryParams.pipe(take(1)).subscribe((params) => {
            const returnUrl = params['returnUrl'];
            if (returnUrl) {
                this.router.navigateByUrl(returnUrl);
            } else {
                this.router.navigate(['/home']);
            }
        });
    }

    oidcLogin() {
        this.showSpinner = true;
        this.route.queryParams.pipe(take(1)).subscribe((params) => {
            const host: string = (window as any)['__env'].apiEndpoint;
            if (params['returnUrl']) {
                window.location.href = `${host}/v1/auth/oidc/login?returnUrl=${params['returnUrl']}`;
            } else {
                window.location.href = `${host}/v1/auth/oidc/login`;
            }
        });
    }

    async login() {
        this.validationErrors = {};

        if (this.credentials.email === '') {
            this.validationErrors['email'] = this.translateService.instant('auth.enter_email');
        }

        if (this.credentials.password === '') {
            this.validationErrors['password'] = this.translateService.instant('auth.enter_password');
        }

        // validate that email is valid
        if (!validateEmail(this.credentials.email)) {
            this.validationErrors['email'] = this.translateService.instant('auth.enter_valid_email');
        }

        // if some validation errors, return
        if (Object.keys(this.validationErrors).length > 0) {
            this.validationErrors['form'] = this.translateService.instant('auth.login_data_invalid');
            this.toastService.showErrorToast(this.translateService.instant('auth.login_failed'), this.translateService.instant('auth.login_data_invalid'));
            return;
        }

        this.showSpinner = true;

        const result = await this.authService.login(this.credentials.email, this.credentials.password).catch((err) => {
            this.showSpinner = false;

            if (err && err.error && err.error.error === 'Login failed' && err.error.message === 'Provided credentials are invalid!') {
                this.validationErrors['form'] = this.translateService.instant('auth.login_data_invalid');
            } else {
                this.validationErrors['form'] = this.translateService.instant('auth.login_failed');
                this.toastService.showErrorToast(this.translateService.instant('auth.login_failed'), this.translateService.instant('auth.login_failed'));
            }
        });

        this.showSpinner = false;
        if (result && result.statusCode === 200 && result.message === 'Login successfull!') {
            this.redirectAfterLogin();
        } else if (result && result.statusCode === 200 && result.message === 'TwoFactor is required!') {
            this.stage = '2fa';
            this.twoFactorDevices = result.data.twoFactorDevices;
            if (result.data.twoFactorDevices.length === 1) {
                this.twofa.selectedDevice = result.data.twoFactorDevices[0].name!;
            }
        }
    }

    onPasswordKeyPress(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.login();
        }
    }
}
